import { useEffect, useRef, useState } from 'react';
import { Input, Dropdown, Option, Spinner, Badge } from '@fluentui/react-components';
import { searchContacts } from '../../services/api';
import type { ContactSearchKind, ContactSearchResult } from '../../types';

interface Props {
  rowId: string;
  onSelect: (id: string, candidate: ContactSearchResult) => void;
}

export default function ContactSearchBox({ rowId, onSelect }: Props) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<ContactSearchKind>('contact');
  const [results, setResults] = useState<ContactSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await searchContacts({ q: q.trim(), kind, take: 20 });
        setResults(r);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q, kind]);

  return (
    <div style={{ marginTop: 8, padding: 8, background: '#f8f8f8', borderRadius: 4 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Input
          placeholder="Search Dataverse"
          value={q}
          onChange={(_, d) => setQ(d.value)}
          style={{ flexGrow: 1 }}
        />
        <Dropdown
          value={kind}
          selectedOptions={[kind]}
          onOptionSelect={(_, d) => setKind(d.optionValue as ContactSearchKind)}
          style={{ minWidth: 110 }}
          aria-label="Search kind"
        >
          <Option value="contact">contacts</Option>
          <Option value="account">accounts</Option>
          <Option value="both">both</Option>
        </Dropdown>
        {loading && <Spinner size="tiny" />}
      </div>
      {results.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {results.map(r => (
            <label
              key={`${rowId}-${r.id}`}
              style={{ display: 'block', padding: '4px 0', cursor: 'pointer' }}
              onClick={() => onSelect(r.id, r)}
            >
              <input type="radio" name={`row-${rowId}-search`} readOnly />{' '}
              {r.fullName}
              {' '}<Badge appearance="outline" size="small">{r.kind}</Badge>
              {r.email && <> &mdash; {r.email}</>}
              {r.phone && <> &mdash; {r.phone}</>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
